import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, AreaField, DateField } from "@/components/fields";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Money, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["repairs", "maintenance", "service_charge", "capex", "utility_bills", "insurance", "security", "cleaning", "other"];
const empty = { property_id: "", landlord_id: "", category: "repairs", amount: 0, narration: "", date: "" };

export default function Expenses() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/expenses").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/landlords").then((r) => setLandlords(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.amount) return toast.error("Amount is required");
    try {
      await api.post("/expenses", { ...form, property_id: form.property_id || null, landlord_id: form.landlord_id || null });
      toast.success("Expense recorded"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;
  const total = items.reduce((s, e) => s + e.amount, 0);

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Costs incurred on behalf of landlords" icon={Money}
        actions={<Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-expense-btn" className="gap-2"><Plus size={16} /> Add Expense</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={Money} title="No expenses yet" message="Record property expenses to reflect on landlord statements."
          action={<Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />Add Expense</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Category</TableHead><TableHead>Property</TableHead><TableHead>Narration</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell><Badge variant="outline" className="capitalize">{String(e.category).replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-sm">{e.property_name || "—"}</TableCell>
                    <TableCell className="text-sm">{e.narration || "—"}</TableCell>
                    <TableCell className="text-sm">{fmtDate(e.date)}</TableCell>
                    <TableCell className="text-right font-mono">{money(e.amount, cur)}</TableCell>
                  </TableRow>
                ))}
                <TableRow><TableCell colSpan={4} className="font-medium text-right">Total</TableCell><TableCell className="text-right font-mono font-semibold">{money(total, cur)}</TableCell></TableRow>
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Category" value={form.category} onChange={(v) => set("category", v)}
              options={CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, " ") }))} testId="expense-category" />
            <TextField label="Amount" type="number" value={form.amount} onChange={(v) => set("amount", v)} required testId="expense-amount" />
            <SelectField label="Property" value={form.property_id} onChange={(v) => set("property_id", v)}
              options={[{ value: "", label: "— None —" }, ...props.map((p) => ({ value: p.id, label: `${p.name} ${p.unit_no || ""}` }))]} />
            <SelectField label="Landlord" value={form.landlord_id} onChange={(v) => set("landlord_id", v)}
              options={[{ value: "", label: "— None —" }, ...landlords.map((l) => ({ value: l.id, label: l.name }))]} />
            <DateField label="Date" value={form.date} onChange={(v) => set("date", v)} />
          </div>
          <div className="mt-4"><AreaField label="Narration" value={form.narration} onChange={(v) => set("narration", v)} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-expense-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
